import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

export interface LinkedAccountsFilters {
  search: string | null;
  integrationName: string | null;
  status: string | null;
  category: string | null;
}

export const EMPTY_LINKED_ACCOUNTS_FILTERS: LinkedAccountsFilters = {
  search: null,
  integrationName: null,
  status: null,
  category: null,
};

const URL_KEYS: Record<keyof LinkedAccountsFilters, string> = {
  search: "search",
  integrationName: "integration__name",
  category: "category",
  status: "status",
};

const FILTER_PARAMS = Object.keys(URL_KEYS) as Array<keyof LinkedAccountsFilters>;

export const useLinkedAccountsFilters = () => {
  const history = useHistory();
  const location = useLocation();

  const filters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return FILTER_PARAMS.reduce<LinkedAccountsFilters>(
      (accumulated, key) => ({ ...accumulated, [key]: params.get(URL_KEYS[key]) }),
      { ...EMPTY_LINKED_ACCOUNTS_FILTERS },
    );
  }, [location.search]);

  const setFilters = useCallback(
    (updated: LinkedAccountsFilters) => {
      const params = new URLSearchParams(location.search);
      FILTER_PARAMS.forEach((key) => {
        const value = updated[key];
        if (value) {
          params.set(URL_KEYS[key], value);
        } else {
          params.delete(URL_KEYS[key]);
        }
      });
      history.replace({ pathname: location.pathname, search: params.toString() });
    },
    [history, location.pathname, location.search],
  );

  const filterSearch = useMemo(() => {
    const params = new URLSearchParams();
    FILTER_PARAMS.forEach((key) => {
      const value = filters[key];
      if (value) params.set(URL_KEYS[key], value);
    });
    return params.toString();
  }, [filters]);

  return { filters, setFilters, filterSearch };
};
